import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Shared base for both Navigator URLs below. Absolute, proxied through Ella to Navigator's
 * Gateway. The path prefix comes from environment.navigatorProxyPrefix -- normally
 * '/navigator-reverse-proxy', but locally it's '' since Ignition's registered local redirect URI
 * (https://localhost:7096/data/federate/callback/oidc) has no path prefix. Must be absolute, not a
 * path relative to the frontend's own origin -- galaxy-mvp0 and Ella are deployed as separate
 * origins, so a bare path would resolve against the frontend's own host, where nothing listens for
 * it.
 *
 * TEMPORARY DEMO PIVOT (2026-10-22) that bypassed this entirely, pointing straight at Navigator's
 * VM, has been reverted -- see Ella/Docs/Navigator/navigator_troubleshooting_log.md for why (a
 * YARP default-forwarder pipeline-ordering bug, not a Gateway-side issue) and
 * navigator_proxy_based_configuration_snapshot.md for the full proxy-based setup this restores.
 */
const NAVIGATOR_BASE = `${environment.baseApiUrl}${environment.navigatorProxyPrefix}`;

/**
 * The real Perspective project -- used ONLY for the embedded iframe, never the popup. Project is
 * "PbS", not "PbS_Dev" -- confirmed both by the redirect URIs already on Ignition's app
 * registration and by a live reachability test against the real Gateway, which returned the
 * actual Perspective client bootstrap HTML with projectName: "PbS". The old "PbS_Dev" value was
 * carried over unexamined from the original pre-proxy hardcoded iframe URL.
 */
export const NAVIGATOR_CONTENT_PATH = `${NAVIGATOR_BASE}/data/perspective/client/PbS/enterprise`;

/**
 * A dedicated, minimal page Ignition built specifically for this flow -- used ONLY for the login
 * popup, never the iframe. On an unauthenticated hit it automatically calls
 * system.perspective.login(forceAuth=False) itself, instead of requiring an explicit click on a
 * login button first (which NAVIGATOR_CONTENT_PATH's own page still does, for anyone who hits it
 * directly outside this embedding flow). It doesn't need to render any real content -- the
 * popup's only job is to establish the Gateway session and get closed once we detect that (see
 * NavigatorResponseTransform's cookie-based completion signal on the backend); the iframe
 * separately loads NAVIGATOR_CONTENT_PATH once that's done.
 *
 * Moved under the "PbS" project (2026-10-23, Ignition dev's own change) -- was previously its own
 * separate top-level project at /data/perspective/client/auto-login. Now nested as a page/view
 * within PbS itself, same project as NAVIGATOR_CONTENT_PATH.
 */
export const NAVIGATOR_AUTO_LOGIN_PATH = `${NAVIGATOR_BASE}/data/perspective/client/PbS/auto-login`;

/**
 * An unauthenticated hit on either URL above makes the Gateway redirect through Entra and back.
 * Routing through Ella's own origin (instead of Navigator's raw VM address) is what makes the
 * resulting Gateway session cookie first-party -- but that only holds if Ella's origin is
 * same-site with the frontend's origin (see navigator_galaxy_implementation_plan.md). As of this
 * writing that requires both sides to sit under the same beusagalaxy.ai registrable domain (or,
 * locally, both being 'localhost' regardless of port); confirm that's true for whichever
 * environment this is running against before trusting the cookie behavior.
 *
 * Hands off a popup window opened synchronously from the sidenav's nav-item click (the only point
 * in this flow guaranteed to run in the same browser task as the user's gesture) to the
 * lazily-loaded NavigatorComponent, which activates asynchronously -- behind a route guard's own
 * backend check and a dynamic import -- well after that gesture window has closed. Calling
 * window.open() that late is unreliable: browsers only honor it as a direct result of a user
 * gesture, and by the time the component's ngOnInit runs, several ticks (and sometimes a real
 * network round trip) separate it from the original click.
 */
@Injectable({ providedIn: 'root' })
export class NavigatorLoginService {
  private pendingPopup: Window | null = null;

  /**
   * Must be called synchronously from the direct result of a user gesture (e.g. a nav-item click
   * handler) -- never behind an await/async step first. Stores the result (or null, if the popup
   * was blocked) for NavigatorComponent to collect once it activates.
   */
  openPopupFromGesture(): void {
    this.pendingPopup = window.open(NAVIGATOR_AUTO_LOGIN_PATH, 'navigator-login', 'width=500,height=700');
  }

  /** Consumes and clears whatever popup (or null) the most recent openPopupFromGesture() produced. */
  takePendingPopup(): Window | null {
    const popup = this.pendingPopup;
    this.pendingPopup = null;
    return popup;
  }
}
